<template>
  <div>
    <div class="back-row">
      <button class="btn-back" @click="$router.go(-1)"><font-awesome-icon icon="arrow-left" /> Back to Requisitions</button>
    </div>

    <div v-if="loading" class="skeleton-wrap">
      <div class="skeleton sk-title"></div>
      <div class="skeleton sk-block"></div>
    </div>

    <template v-else-if="pr">
      <!-- Header -->
      <div class="pr-header">
        <div class="pr-header-info">
          <div class="pr-number-row">
            <span class="mono">{{ pr.pr_number }}</span>
            <span :class="['urgency-badge', `urgency-badge--${pr.urgency}`]">{{ capitalise(pr.urgency) }}</span>
            <span :class="['status-badge', `status-badge--${pr.status}`]">{{ formatStatus(pr.status) }}</span>
          </div>
          <h4 class="pr-title">{{ pr.title }}</h4>
          <p class="pr-meta">
            <span v-if="pr.category">{{ pr.category.name }}</span>
            <span v-if="pr.project"> · Project: {{ pr.project.project_code }} — {{ pr.project.project_name }}</span>
            <span v-if="pr.cost_code"> · {{ pr.cost_code }}</span>
            <span v-if="pr.need_by_date"> · Need by: <strong>{{ formatDate(pr.need_by_date) }}</strong></span>
          </p>
        </div>
        <div class="pr-header-actions">
          <button class="btn-outline" @click="$router.push(`/procurement/purchase-requisitions/${pr.id}/print`)">
            <font-awesome-icon icon="print" class="me-1" /> Preview
          </button>
          <button v-if="['draft','returned'].includes(pr.status)" class="btn-outline" @click="$router.push(`/procurement/purchase-requisitions/${pr.id}/edit`)">
            <font-awesome-icon icon="edit" class="me-1" /> Edit
          </button>
          <button v-if="['draft','returned'].includes(pr.status)" class="btn-submit" :disabled="submitting" @click="submitPR">
            <span v-if="submitting" class="spinner-sm"></span>
            {{ submitting ? 'Submitting...' : 'Submit for Approval' }}
          </button>
          <div v-if="['submitted','under_review'].includes(pr.status)" class="approver-actions">
            <button class="btn-return" :disabled="statusSaving" @click="openReturnModal">
              <font-awesome-icon icon="undo" class="me-1" /> Return
            </button>
          </div>
          <button v-if="pr.status === 'approved'" class="btn-create-rfq" @click="$router.push(`/procurement/sourcing-events/create?pr_id=${pr.id}`)">
            <font-awesome-icon icon="file-alt" class="me-1" /> Create RFQ
          </button>
          <button v-if="pr.status === 'approved'" class="btn-create-po" @click="$router.push(`/procurement/purchase-orders/create?pr_id=${pr.id}`)">
            <font-awesome-icon icon="file-invoice" class="me-1" /> Create Purchase Order
          </button>
        </div>
      </div>

      <!-- Body -->
      <div class="layout-grid">
        <!-- Left: lines + details -->
        <div class="col-main">

          <!-- Line Items -->
          <div class="section-card">
            <h6 class="section-head-text">Line Items</h6>
            <table class="lines-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Qty</th>
                  <th>Ordered</th>
                  <th>UOM</th>
                  <th class="text-right">Unit Cost</th>
                  <th class="text-right">Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(line, i) in pr.lines" :key="line.id"
                    :class="{ 'line-row--ordered': line.status === 'ordered', 'line-row--partial': line.status === 'partially_ordered' }">
                  <td class="line-idx">{{ i + 1 }}</td>
                  <td>
                    <div class="line-desc">{{ line.description }}</div>
                    <div class="line-spec" v-if="line.specification">{{ line.specification }}</div>
                  </td>
                  <td>{{ line.category?.name ?? '—' }}</td>
                  <td>{{ line.quantity }}</td>
                  <td>{{ line.ordered_qty || 0 }}</td>
                  <td>{{ line.uom || '—' }}</td>
                  <td class="text-right">{{ formatCurrency(line.estimated_unit_cost, pr.currency) }}</td>
                  <td class="text-right font-bold">{{ formatCurrency(line.estimated_total, pr.currency) }}</td>
                  <td>
                    <span :class="['line-ord-badge', `line-ord-badge--${line.status || 'available'}`]">
                      {{ { available: 'Available', partially_ordered: 'Partial', ordered: 'Ordered' }[line.status || 'available'] }}
                    </span>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="6" class="text-right total-label-cell">Estimated Total</td>
                  <td class="text-right total-value-cell">{{ formatCurrency(pr.estimated_total, pr.currency) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- Purchase Orders generated from this PR -->
          <div class="section-card" v-if="pr.purchase_orders && pr.purchase_orders.length">
            <div class="po-section-hd">
              <h6 class="section-head-text">Purchase Orders</h6>
              <button v-if="['approved','sourcing','partially_sourced'].includes(pr.status)" class="btn-create-po btn-create-po--sm"
                      @click="$router.push(`/procurement/purchase-orders/create?pr_id=${pr.id}`)">
                <font-awesome-icon icon="plus" /> New PO
              </button>
            </div>
            <table class="po-ref-table">
              <thead>
                <tr>
                  <th>PO Number</th>
                  <th>Vendor</th>
                  <th class="text-right">Total</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="po in pr.purchase_orders" :key="po.id">
                  <td class="mono-sm">{{ po.po_number }}</td>
                  <td>{{ po.vendor?.name || '—' }}</td>
                  <td class="text-right">{{ formatCurrency(po.total, po.currency) }}</td>
                  <td><span :class="['po-status-badge', `po-status-badge--${po.status}`]">{{ formatPoStatus(po.status) }}</span></td>
                  <td>
                    <button class="btn-view-po" @click="$router.push(`/procurement/purchase-orders/${po.id}`)">View</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Description / Notes -->
          <div class="section-card" v-if="pr.description || pr.notes">
            <h6 class="section-head-text">Additional Information</h6>
            <div v-if="pr.description" class="detail-block">
              <span class="detail-label">Description</span>
              <p class="detail-text">{{ pr.description }}</p>
            </div>
            <div v-if="pr.notes" class="detail-block mt-2">
              <span class="detail-label">Notes</span>
              <p class="detail-text">{{ pr.notes }}</p>
            </div>
            <div v-if="pr.return_reason" class="return-reason-box">
              <font-awesome-icon icon="exclamation-triangle" class="me-1" />
              <strong>Return Reason:</strong> {{ pr.return_reason }}
            </div>
          </div>
        </div>

        <!-- Right: meta + approval -->
        <div class="col-side">
          <!-- Details -->
          <div class="section-card">
            <h6 class="section-head-text">Details</h6>
            <div class="meta-list">
              <div class="meta-item"><span class="meta-label">Requester</span><span class="meta-value">{{ requesterName }}</span></div>
              <div class="meta-item"><span class="meta-label">Category</span><span class="meta-value">{{ pr.category?.name ?? '—' }}</span></div>
              <div class="meta-item"><span class="meta-label">Department</span><span class="meta-value">{{ pr.department?.name || '—' }}</span></div>
              <div class="meta-item"><span class="meta-label">Project</span><span class="meta-value">{{ pr.project ? pr.project.project_code + ' — ' + pr.project.project_name : '—' }}</span></div>
              <div class="meta-item"><span class="meta-label">Cost Code</span><span class="meta-value">{{ pr.cost_code || '—' }}</span></div>
              <div class="meta-item"><span class="meta-label">Need By</span><span class="meta-value" :class="isOverdue(pr.need_by_date) ? 'text-danger' : ''">{{ pr.need_by_date ? formatDate(pr.need_by_date) : '—' }}</span></div>
              <div class="meta-item"><span class="meta-label">Currency</span><span class="meta-value">{{ pr.currency || '—' }}</span></div>
              <div class="meta-item"><span class="meta-label">Created</span><span class="meta-value">{{ formatDate(pr.created_at) }}</span></div>
            </div>
          </div>

          <!-- Approval Status -->
          <div class="section-card" v-if="pr.approval_request">
            <h6 class="section-head-text">Approval</h6>
            <div class="approval-status-row">
              <span :class="['appr-badge', `appr-badge--${pr.approval_request.status}`]">{{ formatApprovalStatus(pr.approval_request.status) }}</span>
              <span class="appr-step">Step {{ pr.approval_request.current_step }}</span>
            </div>
            <div class="approval-steps" v-if="pr.approval_request.steps?.length">
              <div v-for="step in pr.approval_request.steps" :key="step.id" class="appr-step-row">
                <div :class="['appr-dot', `appr-dot--${step.status}`]"></div>
                <div class="appr-step-info">
                  <div class="appr-step-name">Step {{ step.step_order }}: {{ step.position_name || 'Approver' }}</div>
                  <div class="appr-step-meta">
                    <span :class="['appr-step-status', `appr-step-status--${step.status}`]">{{ capitalise(step.status) }}</span>
                    <span v-if="step.acted_at" class="appr-step-date"> · {{ formatDate(step.acted_at) }}</span>
                    <p v-if="step.comments" class="appr-step-comment">{{ step.comments }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="section-card" v-else-if="['submitted','under_review','approved'].includes(pr.status)">
            <h6 class="section-head-text">Approval</h6>
            <p class="text-muted-sm">No approval workflow configured for purchase requisitions.</p>
          </div>
        </div>
      </div>
    </template>

    <!-- Return Modal -->
    <div v-if="returnModalVisible" class="modal-backdrop" @click.self="returnModalVisible = false">
      <div class="modal-box">
        <div class="modal-header">
          <h6>Return Requisition</h6>
          <button class="btn-close-modal" @click="returnModalVisible = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="field-group">
            <label class="field-label">Reason for Return <span class="req">*</span></label>
            <textarea v-model="returnReason" class="field-input" rows="4" placeholder="Explain what needs to be corrected..."></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="returnModalVisible = false">Cancel</button>
          <button class="btn-return" :disabled="statusSaving" @click="updateStatus('returned')">
            {{ statusSaving ? 'Saving...' : 'Return for Revision' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'PrDetail',
  setup() { return { toast: useToast() }; },
  data() {
    return {
      pr: null, loading: true,
      submitting: false,
      statusSaving: false,
      returnModalVisible: false,
      returnReason: '',
    };
  },
  computed: {
    requesterName() {
      if (!this.pr?.requester) return '—';
      const r = this.pr.requester;
      return [r.first_name, r.last_name].filter(Boolean).join(' ') || '—';
    },
  },
  mounted() { this.fetchPR(); },
  methods: {
    async fetchPR() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/procurement/purchase-requisitions/${this.$route.params.id}`);
        this.pr = data.data;
      } catch { this.toast.error('Failed to load requisition'); }
      finally { this.loading = false; }
    },

    async submitPR() {
      if (!confirm('Submit this requisition for approval?')) return;
      this.submitting = true;
      try {
        await axios.post(`/procurement/purchase-requisitions/${this.pr.id}/submit`);
        this.toast.success('Requisition submitted');
        await this.fetchPR();
      } catch (e) { this.toast.error(e.response?.data?.message || 'Failed to submit'); }
      finally { this.submitting = false; }
    },

    openReturnModal() { this.returnReason = ''; this.returnModalVisible = true; },

    async updateStatus(status) {
      if (status === 'returned' && !this.returnReason.trim()) {
        this.toast.error('Please provide a return reason');
        return;
      }
      this.statusSaving = true;
      try {
        await axios.patch(`/procurement/purchase-requisitions/${this.pr.id}/status`, { status, reason: this.returnReason });
        this.toast.success('Status updated');
        this.returnModalVisible = false;
        await this.fetchPR();
      } catch (e) { this.toast.error(e.response?.data?.message || 'Failed'); }
      finally { this.statusSaving = false; }
    },

    formatStatus(s) {
      const map = { draft:'Draft', submitted:'Submitted', under_review:'Under Review', returned:'Returned',
        approved:'Approved', sourcing:'Sourcing', partially_sourced:'Partially Sourced',
        fully_sourced:'Fully Sourced', closed:'Closed', cancelled:'Cancelled' };
      return map[s] ?? s;
    },
    formatApprovalStatus(s) {
      return { pending:'Pending', in_progress:'In Progress', approved:'Approved', rejected:'Rejected', cancelled:'Cancelled' }[s] ?? s;
    },
    capitalise(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; },
    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
    },
    formatCurrency(amount, currency) {
      const sym = { NGN:'₦', GBP:'£', USD:'$', EUR:'€' }[currency] ?? (currency ?? '');
      return sym + Number(amount || 0).toLocaleString('en', { minimumFractionDigits:2, maximumFractionDigits:2 });
    },
    isOverdue(d) { return d && new Date(d) < new Date(); },
    formatPoStatus(s) {
      return { draft:'Draft', issued:'Issued', partially_received:'Partial', received:'Received', cancelled:'Cancelled' }[s] ?? s;
    },
  },
};
</script>

<style scoped>
.back-row  { margin-bottom:14px; }
.btn-back  { background:transparent; border:none; color:#7a90b0; font-size:13px; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:7px; padding:0; }
.btn-back:hover { color:#004AAD; }

.skeleton-wrap { display:flex; flex-direction:column; gap:16px; }
.skeleton { background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:8px; }
.sk-title { height:36px; width:50%; }
.sk-block { height:200px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.pr-header { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; margin-bottom:16px; flex-wrap:wrap; }
.pr-number-row { display:flex; align-items:center; gap:8px; margin-bottom:6px; flex-wrap:wrap; }
.mono { font-family:monospace; font-size:13px; font-weight:700; color:#004AAD; }
.pr-title { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.pr-meta  { font-size:13px; color:#7a90b0; margin:0; }
.pr-header-actions { display:flex; gap:10px; flex-wrap:wrap; flex-shrink:0; align-items:flex-start; }
.approver-actions  { display:flex; gap:8px; }
.me-1 { margin-right:4px; }

.layout-grid { display:grid; grid-template-columns:1fr 320px; gap:16px; align-items:start; }
.col-main { display:flex; flex-direction:column; gap:16px; }
.col-side { display:flex; flex-direction:column; gap:16px; }

.section-card    { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
.section-head-text { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 16px; }

.lines-table { width:100%; border-collapse:collapse; font-size:13px; }
.lines-table th { padding:9px 12px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; }
.lines-table td { padding:11px 12px; border-bottom:1px solid #f0f4f8; vertical-align:top; color:#1a2b45; }
.lines-table tfoot td { border-top:2px solid #e4eaf2; border-bottom:none; padding-top:14px; }
.line-idx      { color:#aab8cc; font-size:12px; width:28px; }
.line-desc     { font-weight:600; }
.line-spec     { font-size:11.5px; color:#7a90b0; margin-top:2px; }
.text-right    { text-align:right; }
.font-bold     { font-weight:700; }
.total-label-cell { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.total-value-cell { font-size:16px; font-weight:800; color:#1a2b45; }

.detail-block  { display:flex; flex-direction:column; gap:4px; }
.detail-label  { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.detail-text   { font-size:13.5px; color:#1a2b45; margin:0; }
.mt-2          { margin-top:14px; }
.return-reason-box { background:#fff7ed; border:1px solid #fed7aa; border-radius:8px; padding:12px 14px; font-size:13px; color:#c2410c; margin-top:14px; }

.meta-list  { display:flex; flex-direction:column; gap:12px; }
.meta-item  { display:flex; justify-content:space-between; align-items:flex-start; gap:8px; }
.meta-label { font-size:12px; font-weight:600; color:#7a90b0; text-transform:uppercase; letter-spacing:.04em; flex-shrink:0; }
.meta-value { font-size:13px; color:#1a2b45; text-align:right; }
.text-danger { color:#dc2626; font-weight:600; }
.text-muted-sm { font-size:12.5px; color:#aab8cc; }

.approval-status-row { display:flex; align-items:center; gap:10px; margin-bottom:16px; }
.appr-badge { display:inline-block; font-size:12px; font-weight:600; border-radius:20px; padding:4px 12px; }
.appr-badge--pending     { background:#f1f5f9; color:#64748b; }
.appr-badge--in_progress { background:#fff7ed; color:#c2410c; }
.appr-badge--approved    { background:#dcfce7; color:#15803d; }
.appr-badge--rejected    { background:#fee2e2; color:#dc2626; }
.appr-badge--cancelled   { background:#f1f5f9; color:#64748b; }
.appr-step   { font-size:12px; color:#7a90b0; }

.approval-steps    { display:flex; flex-direction:column; gap:14px; }
.appr-step-row     { display:flex; gap:10px; }
.appr-dot          { width:10px; height:10px; border-radius:50%; flex-shrink:0; margin-top:5px; }
.appr-dot--pending  { background:#dde5f0; }
.appr-dot--in_progress { background:#f97316; }
.appr-dot--approved { background:#16a34a; }
.appr-dot--rejected { background:#dc2626; }
.appr-dot--skipped  { background:#aab8cc; }
.appr-step-info    { flex:1; }
.appr-step-name    { font-size:13px; font-weight:600; color:#1a2b45; }
.appr-step-meta    { font-size:12px; color:#7a90b0; margin-top:2px; }
.appr-step-status  { font-weight:600; }
.appr-step-status--approved { color:#16a34a; }
.appr-step-status--rejected { color:#dc2626; }
.appr-step-status--in_progress { color:#f97316; }
.appr-step-date    { color:#aab8cc; }
.appr-step-comment { font-size:12px; color:#7a90b0; margin:4px 0 0; font-style:italic; }

.urgency-badge { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; }
.urgency-badge--normal    { background:#f1f5f9; color:#64748b; }
.urgency-badge--urgent    { background:#fff7ed; color:#c2410c; }
.urgency-badge--emergency { background:#fee2e2; color:#dc2626; }

.status-badge { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; }
.status-badge--draft         { background:#f1f5f9; color:#64748b; }
.status-badge--submitted     { background:#eff6ff; color:#1d4ed8; }
.status-badge--under_review  { background:#fff7ed; color:#c2410c; }
.status-badge--returned      { background:#fef9c3; color:#854d0e; }
.status-badge--approved      { background:#dcfce7; color:#15803d; }
.status-badge--sourcing      { background:#f0f5ff; color:#004AAD; }
.status-badge--partially_sourced { background:#e0f2fe; color:#0369a1; }
.status-badge--fully_sourced      { background:#d1fae5; color:#065f46; }
.status-badge--partially_received { background:#fef3c7; color:#92400e; }
.status-badge--received           { background:#dcfce7; color:#166534; }
.status-badge--closed             { background:#f1f5f9; color:#475569; }
.status-badge--cancelled     { background:#fee2e2; color:#dc2626; }

.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover:not(:disabled) { background:#f4f7fb; }
.btn-submit  { display:inline-flex; align-items:center; gap:6px; background:#16a34a; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; }
.btn-submit:hover:not(:disabled)  { background:#15803d; }
.btn-submit:disabled  { opacity:.5; cursor:not-allowed; }
.btn-approve { display:inline-flex; align-items:center; gap:6px; background:#16a34a; color:#fff; font-size:13px; font-weight:600; padding:9px 16px; border-radius:8px; border:none; cursor:pointer; }
.btn-approve:hover:not(:disabled) { background:#15803d; }
.btn-approve:disabled { opacity:.5; cursor:not-allowed; }
.btn-return    { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#c2410c; border:1.5px solid #fed7aa; font-size:13px; font-weight:600; padding:9px 16px; border-radius:8px; cursor:pointer; }
.btn-return:hover:not(:disabled) { background:#fff7ed; }
.btn-return:disabled { opacity:.5; cursor:not-allowed; }
.btn-create-rfq { display:inline-flex; align-items:center; gap:6px; background:#6d28d9; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; }
.btn-create-rfq:hover { background:#5b21b6; }
.btn-create-po { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; }
.btn-create-po:hover { background:#003a8c; }
.btn-create-po--sm { padding:5px 12px; font-size:12px; }
.po-section-hd { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
.po-section-hd .section-head-text { margin:0; }
.po-ref-table { width:100%; border-collapse:collapse; font-size:13px; }
.po-ref-table th { background:#f4f7fb; font-weight:600; color:#5a6a85; padding:8px 12px; text-align:left; font-size:12px; }
.po-ref-table td { padding:9px 12px; border-top:1px solid #f0f4fa; color:#1a2b45; }
.mono-sm { font-family:monospace; font-size:12.5px; }
.btn-view-po { background:none; border:1px solid #c7d9f9; color:#004AAD; font-size:12px; font-weight:600; padding:3px 10px; border-radius:6px; cursor:pointer; }
.btn-view-po:hover { background:#eff6ff; }
.po-status-badge { display:inline-block; font-size:11px; font-weight:700; border-radius:20px; padding:2px 8px; }
.po-status-badge--draft              { background:#f1f5ff; color:#004AAD; }
.po-status-badge--issued             { background:#fef9c3; color:#854d0e; }
.po-status-badge--partially_received { background:#fef3c7; color:#92400e; }
.po-status-badge--received           { background:#dcfce7; color:#166534; }
.po-status-badge--cancelled          { background:#fee2e2; color:#991b1b; }
/* PR line order status badges */
.line-ord-badge { display:inline-block; font-size:11px; font-weight:700; border-radius:20px; padding:2px 8px; white-space:nowrap; }
.line-ord-badge--available         { background:#dcfce7; color:#166534; }
.line-ord-badge--partially_ordered { background:#fef3c7; color:#92400e; }
.line-ord-badge--ordered           { background:#e0e7ff; color:#3730a3; }
.line-row--ordered  { background:#f9fafb; opacity:.55; }
.line-row--partial  { background:#fffbeb; }

.spinner-sm { width:14px; height:14px; border:2px solid rgba(255,255,255,.4); border-top-color:#fff; border-radius:50%; animation:spin .7s linear infinite; display:inline-block; }
@keyframes spin { to { transform:rotate(360deg); } }

.modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; }
.modal-box  { background:#fff; border-radius:14px; width:100%; max-width:500px; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid #e4eaf2; }
.modal-header h6 { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.btn-close-modal { background:transparent; border:none; font-size:22px; color:#7a90b0; cursor:pointer; }
.modal-body { padding:24px; }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #e4eaf2; }
.field-group { display:flex; flex-direction:column; gap:5px; }
.field-label { font-size:13px; font-weight:600; color:#1a2b45; }
.req   { color:#e03535; }
.field-input { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; font-family:inherit; box-sizing:border-box; }
.field-input:focus { border-color:#004AAD; }

@media(max-width:900px) {
  .layout-grid { grid-template-columns:1fr; }
  .col-side    { order:-1; }
}
@media(max-width:600px) {
  .pr-header      { flex-direction:column; }
  .pr-header-actions { width:100%; }
  .lines-table th:nth-child(3), .lines-table td:nth-child(3),
  .lines-table th:nth-child(5), .lines-table td:nth-child(5) { display:none; }
}
</style>
